import { useEffect, useRef, useState, useMemo } from "react";
import { MoveCounter, Timer } from "@solitaire/ui";
import { getScoreBreakdown } from "@solitaire/game-engine";
import type { ScoreBreakdown } from "@solitaire/game-engine";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GameBoard } from "../components/GameBoard.js";
import { AuthArea } from "../components/AuthArea.js";
import { useGameStore } from "../store/useGameStore.js";
import { useAuthStore } from "../store/useAuthStore.js";
import { saveScore } from "../lib/scores.js";
import { loadJourneySet, saveJourneySet } from "../lib/journey.js";
import { GAME_CONFIG } from "../config.js";
import { useI18n } from "../i18n/index.js";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Seed aus heutigem Datum: 20260203 für 3. Feb 2026 */
function getDailySeed(): number {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

/** Datum aus daily-Seed formatieren: 20260203 → "03.02.2026" (de) / "02/03/2026" (en) */
function formatDailySeed(seed: number, locale: string): string {
  const s = String(seed);
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  return locale === "de" ? `${d}.${m}.${y}` : `${m}/${d}/${y}`;
}

// ============================================================
// GamePage
// ============================================================

export function GamePage() {
  const { gameState, currentSetNumber, retrySet, nextSet, undo, tick, newGame, _journeyInitDone, _maxReachedSet, drawMode } = useGameStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();

  const mode = params.get("mode");
  const isDaily = mode === "daily";
  const isJourney = mode === "journey" || mode === "journey-klondike";
  const isKlondike = mode === "klondike" || mode === "journey-klondike";
  const isJourneyKlondike = mode === "journey-klondike";
  const dailySeed = getDailySeed();

  // Daily mode: beim Mount den Tages-Seed setzen (nur wenn noch nicht geladen)
  const dailyInitRef = useRef(false);
  useEffect(() => {
    if (isDaily && !dailyInitRef.current) {
      dailyInitRef.current = true;
      if (currentSetNumber !== dailySeed || drawMode !== 1) {
        newGame(dailySeed, 1);
      }
    }
  }, [isDaily, dailySeed, currentSetNumber, drawMode, newGame]);

  // Klondike daily mode: beim Mount den Tages-Seed mit drawMode=3 setzen
  // (journey-klondike wird vom Journey-Init behandelt)
  const klondikeInitRef = useRef(false);
  useEffect(() => {
    if (mode === "klondike" && !klondikeInitRef.current) {
      klondikeInitRef.current = true;
      newGame(dailySeed, 3);
    }
  }, [mode, dailySeed, newGame]);

  // Journey mode: beim Mount den gespeicherten Fortschritt laden
  // _journeyInitDone lebt im Store → überlebt Remounts (z.B. Leaderboard → zurück)
  useEffect(() => {
    if (isJourney && !_journeyInitDone) {
      useGameStore.setState({ _journeyInitDone: true });
      const dm = isJourneyKlondike ? 3 : 1;
      loadJourneySet(user?.id ?? null).then((set) => {
        if (currentSetNumber !== set || drawMode !== dm) {
          newGame(set, dm);
        }
      });
    }
  }, [isJourney, isJourneyKlondike, user, currentSetNumber, drawMode, newGame, _journeyInitDone]);

  // Freier Modus (kein mode-Param): drawMode auf 1 zurücksetzen falls nötig
  const freeInitRef = useRef(false);
  useEffect(() => {
    if (!mode && !freeInitRef.current) {
      freeInitRef.current = true;
      if (drawMode !== 1) {
        newGame(undefined, 1);
      }
    }
  }, [mode, drawMode, newGame]);

  const numFmt = locale === "de" ? "de-DE" : "en-US";

  const isWon = gameState.status === "won";
  const undoLimit = GAME_CONFIG.maxUndoCount;
  const undoExhausted = undoLimit > 0 && gameState.undoCount >= undoLimit;

  const scoring = GAME_CONFIG.scoring;

  // Live-Score: wird bei jedem Zug + jeder Timer-Sekunde neu berechnet
  const liveBreakdown = useMemo(
    () => getScoreBreakdown(gameState, scoring, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gameState]
  );
  const breakdown = isWon ? liveBreakdown : null;

  // Verdienter Score (ohne Zeitbonus + Gewinn-Bonus — startet bei 0, zählt mit Zügen hoch)
  const earnedScore =
    liveBreakdown.foundationBonus +
    liveBreakdown.flipBonus -
    liveBreakdown.rotationPenalty -
    liveBreakdown.shakePenalty;

  // +N-Flash: merken wann sich der verdiente Score ändert
  const [scoreFlash, setScoreFlash] = useState<{ delta: number; key: number } | null>(null);
  const prevScoreRef = useRef<number>(0);
  useEffect(() => {
    if (gameState.status === "idle") { prevScoreRef.current = 0; return; }
    const delta = earnedScore - prevScoreRef.current;
    prevScoreRef.current = earnedScore;
    if (delta !== 0) {
      setScoreFlash({ delta, key: Date.now() });
    }
  }, [earnedScore, gameState.status]);

  // Ende-Overlay: beim Klick einfrieren — Timer läuft danach nicht mehr in die Auswertung
  const [endBreakdown, setEndBreakdown] = useState<ScoreBreakdown | null>(null);
  const [endRank,   setEndRank]   = useState<number | null>(null);
  const [endSaving, setEndSaving] = useState(false);

  const handleShowEnd = () => {
    const frozen = getScoreBreakdown(gameState, scoring, false);
    setEndBreakdown(frozen);
    setEndRank(null);
    if (user) {
      setEndSaving(true);
      saveScore(gameState, user.id, scoring, false, drawMode)
        .then(({ rank, error }) => {
          if (error) console.error("[GamePage] saveScore (Ende) error:", error);
          setEndRank(rank);
        })
        .catch((err) => console.error("[GamePage] saveScore (Ende) exception:", err))
        .finally(() => setEndSaving(false));
    }
  };

  // Help-Overlay
  const [showHelp, setShowHelp] = useState(false);
  const helpTextKey = isJourneyKlondike ? "game.helpJourneyKlondike" : isKlondike ? "game.helpKlondike" : isDaily ? "game.helpDaily" : isJourney ? "game.helpJourney" : "game.helpDefault";

  // Set-Picker-Overlay
  const [showSetPicker, setShowSetPicker] = useState(false);
  const [setPickerValue, setSetPickerValue] = useState("");

  const handleRetry = () => { setEndBreakdown(null); setEndRank(null); setEndSaving(false); retrySet(); };
  const handleNext  = () => {
    setEndBreakdown(null); setEndRank(null); setEndSaving(false);
    const nextNumber = currentSetNumber + 1;
    nextSet();
    if (isJourney) {
      saveJourneySet(user?.id ?? null, nextNumber);
    }
  };

  const handleJumpToSet = () => {
    const n = parseInt(setPickerValue, 10);
    if (isNaN(n) || n < 1 || n > _maxReachedSet) return;
    setShowSetPicker(false);
    setEndBreakdown(null); setEndRank(null); setEndSaving(false);
    newGame(n);
  };

  const [savedRank, setSavedRank] = useState<number | null>(null);
  const savedForSeedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isWon || !user || savedForSeedRef.current === gameState.seed) return;
    savedForSeedRef.current = gameState.seed;
    saveScore(gameState, user.id, scoring, true, drawMode)
      .then(({ rank, error }) => {
        if (error) console.error("[GamePage] saveScore error:", error);
        setSavedRank(rank);
      })
      .catch((err) => console.error("[GamePage] saveScore exception:", err));
  }, [isWon, user, gameState, scoring]);

  useEffect(() => {
    if (!isWon) {
      setSavedRank(null);
      setEndBreakdown(null);
      setEndRank(null);
      setEndSaving(false);
      savedForSeedRef.current = null;
    }
  }, [isWon]);

  // Journey: _maxReachedSet aus DB aktualisieren (falls DB-Wert höher ist)
  useEffect(() => {
    if (isJourney) {
      loadJourneySet(user?.id ?? null).then((journeySet) => {
        const current = useGameStore.getState()._maxReachedSet;
        if (journeySet > current) {
          useGameStore.setState({ _maxReachedSet: journeySet });
        }
      });
    }
  }, [isJourney, user]);

  // Hilfswerte für Overlays
  const foundationCount = (bd: ScoreBreakdown) =>
    Math.round(bd.foundationBonus / scoring.pointsPerFoundationCard);
  const extraRotations = Math.max(0, gameState.stockRecycleCount - scoring.rotationThreshold);
  const remainingSeconds = Math.max(0, scoring.thresholdSeconds - gameState.elapsedSeconds);
  const pickerNum = parseInt(setPickerValue, 10);
  const pickerValid = !isNaN(pickerNum) && pickerNum >= 1 && pickerNum <= _maxReachedSet;

  return (
    <div className="h-full flex flex-col text-white select-none">
      {/* Header */}
      <header className="bg-black/20 shrink-0 px-3 pt-2 pb-1">
        <div className="flex items-center justify-between">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-3">
              <MoveCounter moves={gameState.moves} className="text-white text-sm" />
              <Timer
                running={gameState.status === "playing"}
                initialSeconds={gameState.elapsedSeconds}
                onTick={() => tick()}
                className="text-white text-sm"
              />
            </div>
            <div className="flex items-center gap-1.5 relative">
              <span className="text-yellow-300 text-[1.5rem] font-bold tabular-nums leading-tight">
                {Math.max(0, earnedScore).toLocaleString(numFmt)} {t("game.pts")}
              </span>
              {scoreFlash && (
                <span
                  key={scoreFlash.key}
                  className={[
                    "absolute left-full ml-1.5 text-xs font-bold px-1 rounded",
                    "animate-score-flash pointer-events-none whitespace-nowrap",
                    scoreFlash.delta > 0 ? "text-green-300" : "text-red-300",
                  ].join(" ")}
                  onAnimationEnd={() => setScoreFlash(null)}
                >
                  {scoreFlash.delta > 0 ? "+" : ""}{scoreFlash.delta}
                </span>
              )}
            </div>
          </div>

          <div className="text-center leading-tight">
            <h1 className="text-xs font-semibold tracking-widest text-white/50 uppercase">Solitaire</h1>
            <p className="text-lg font-bold tracking-wide">
              {(isDaily && !isJourneyKlondike) || mode === "klondike" ? formatDailySeed(dailySeed, locale) : `Set ${currentSetNumber.toLocaleString(numFmt)}`}
            </p>
            {isKlondike && (
              <p className="text-white/40 text-[10px] uppercase tracking-widest">Klondike</p>
            )}
          </div>

          <div className="min-w-0">
            <AuthArea />
          </div>
        </div>

        <div className="flex items-center justify-center gap-1.5 mt-1 pb-1">
          <button
            onClick={() => navigate("/start")}
            className="p-1.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
            title={t("game.homeTitle")}
            aria-label={t("game.homeTitle")}
          >
            <img src="/images/btn_home.png" alt="" className="h-5 w-5 object-contain" />
          </button>
          <button
            onClick={undo}
            disabled={undoExhausted}
            className={[
              "p-1.5 rounded transition-colors",
              undoExhausted
                ? "bg-white/5 opacity-30 cursor-not-allowed"
                : "bg-white/15 hover:bg-white/25",
            ].join(" ")}
            title={
              undoExhausted
                ? t("game.undoLimitReached", { limit: undoLimit })
                : undoLimit > 0
                  ? t("game.undoCount", { used: gameState.undoCount, limit: undoLimit })
                  : t("game.undoMove")
            }
            aria-label={t("game.undoMove")}
          >
            <img src="/images/btn_back.png" alt="" className="h-5 w-5 object-contain" />
          </button>
          <button
            onClick={() => setShowHelp(true)}
            className="p-1.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
            title={t("game.helpTitle")}
            aria-label={t("game.helpTitle")}
          >
            <img src="/images/btn_help.png" alt="" className="h-5 w-5 object-contain" />
          </button>
          <button
            onClick={handleRetry}
            className="p-1.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
            title={t("game.retryTitle")}
            aria-label={t("game.retry")}
          >
            <img src="/images/btn_redo.png" alt="" className="h-5 w-5 object-contain" />
          </button>
          {!isDaily && mode !== "klondike" && (
            <button
              onClick={() => { setSetPickerValue(String(currentSetNumber + 1)); setShowSetPicker(true); }}
              className="p-1.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
              title={t("game.nextSetTitle")}
              aria-label={t("game.nextSet")}
            >
              <img src="/images/btn_next.png" alt="" className="h-5 w-5 object-contain" />
            </button>
          )}
          {!isWon && (
            <button
              onClick={handleShowEnd}
              className="px-3 py-1 rounded bg-red-500/30 hover:bg-red-500/50 text-red-200 text-sm transition-colors"
              title={t("game.endTitle")}
            >
              {t("game.end")}
            </button>
          )}
          <button
            onClick={() => navigate(`/leaderboard?seed=${currentSetNumber}${drawMode === 3 ? "&drawMode=3" : ""}`)}
            className="p-1.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
            title={t("game.leaderboardTitle")}
            aria-label={t("game.leaderboardTitle")}
          >
            <img src="/images/btn_score.png" alt="" className="h-5 w-5 object-contain" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <GameBoard />
      </main>

      {/* Ende-Overlay (Set abgebrochen) */}
      {endBreakdown && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-50 p-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 shadow-2xl w-full max-w-xs">
            <p className="text-4xl mb-1">🏁</p>
            <h2 className="text-xl font-bold mb-0.5">{t("end.title")}</h2>
            <p className="text-white/50 text-xs mb-3">
              {gameState.moves} {t("game.moves")} · {formatTime(gameState.elapsedSeconds)}
            </p>

            <div className="text-sm w-full mb-3 space-y-0.5">
              <div className="flex justify-between">
                <span className="text-white/70">{t("end.timeBonusReduced", { divisor: scoring.notCompletedDivisor })}</span>
                <span className="text-green-300 font-medium">+{endBreakdown.timeBonus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t("end.foundation", { count: foundationCount(endBreakdown) })}</span>
                <span className="text-green-300 font-medium">+{endBreakdown.foundationBonus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t("end.flips", { count: gameState.flipCount })}</span>
                <span className="text-green-300 font-medium">+{endBreakdown.flipBonus}</span>
              </div>

              {(endBreakdown.rotationPenalty > 0 || endBreakdown.shakePenalty > 0) && (
                <div className="border-t border-white/15 my-1" />
              )}
              {endBreakdown.rotationPenalty > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/70">{t("end.rotations", { count: extraRotations })}</span>
                  <span className="text-red-300 font-medium">−{endBreakdown.rotationPenalty}</span>
                </div>
              )}
              {endBreakdown.shakePenalty > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/70">{t("end.shakes", { count: gameState.shakeCount })}</span>
                  <span className="text-red-300 font-medium">−{endBreakdown.shakePenalty}</span>
                </div>
              )}

              <div className="border-t border-white/30 pt-1.5 mt-1 flex justify-between items-baseline">
                <span className="text-white/80 font-semibold">{t("end.total")}</span>
                <span className="text-yellow-300 text-2xl font-bold">{endBreakdown.total.toLocaleString(numFmt)}</span>
              </div>
            </div>

            {user && (
              <p className="text-sm mb-3">
                {endSaving
                  ? <span className="text-white/40">{t("end.saving")}</span>
                  : endRank !== null
                    ? <span className="text-yellow-300 font-bold">{t("end.rank", { rank: endRank })}</span>
                    : <span className="text-white/40">{t("end.saved")}</span>
                }
              </p>
            )}

            <div className="flex flex-col gap-2">
              {user && (
                <button
                  onClick={() => { setEndBreakdown(null); navigate(`/leaderboard?seed=${gameState.seed}${drawMode === 3 ? "&drawMode=3" : ""}`); }}
                  className="px-6 py-2 rounded-lg bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-300 text-sm font-medium transition-colors border border-yellow-400/30"
                >
                  {t("end.showLeaderboard")}
                </button>
              )}
              {!isDaily && mode !== "klondike" && (
                <button onClick={handleNext} className="px-6 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
                  {t("end.nextSet")}
                </button>
              )}
              <button onClick={handleRetry} className="px-4 py-1.5 rounded-lg text-white/50 hover:text-white/80 text-xs transition-colors">
                {t("end.retry")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help-Overlay */}
      {showHelp && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/60 z-50 p-4"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 shadow-2xl w-full max-w-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-3xl mb-2">❓</p>
            <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed text-left mb-4">
              {t(helpTextKey as Parameters<typeof t>[0])}
            </p>
            <button
              onClick={() => setShowHelp(false)}
              className="px-6 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
            >
              {t("game.helpClose")}
            </button>
          </div>
        </div>
      )}

      {/* Set-Picker-Overlay */}
      {showSetPicker && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/60 z-50 p-4"
          onClick={() => setShowSetPicker(false)}
        >
          <div
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 shadow-2xl w-full max-w-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-4">{t("game.pickSetTitle")}</h2>

            <button
              onClick={() => { setShowSetPicker(false); handleNext(); }}
              className="w-full px-6 py-2.5 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors mb-4"
            >
              {t("game.pickSetNext", { next: (currentSetNumber + 1).toLocaleString(numFmt) })}
            </button>

            <p className="text-white/40 text-xs mb-3">{t("game.pickSetOr")}</p>

            <div className="flex gap-2 mb-2">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={_maxReachedSet}
                value={setPickerValue}
                onChange={(e) => setSetPickerValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleJumpToSet(); }}
                className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-center text-lg font-mono outline-none focus:border-white/40"
                placeholder={t("game.pickSetPlaceholder")}
                autoFocus
              />
              <button
                onClick={handleJumpToSet}
                disabled={!pickerValid}
                className="px-4 py-2 rounded-lg bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-300 font-medium transition-colors border border-yellow-400/30 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {t("game.pickSetGo")}
              </button>
            </div>

            <p className="text-white/30 text-xs">
              {t("game.pickSetRange", { max: _maxReachedSet.toLocaleString(numFmt) })}
            </p>
          </div>
        </div>
      )}

      {/* Gewinn-Overlay */}
      {isWon && breakdown && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-50 p-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 shadow-2xl w-full max-w-xs">
            <p className="text-4xl mb-1">🎉</p>
            <h2 className="text-xl font-bold mb-0.5">{t("win.title")}</h2>
            <p className="text-white/50 text-xs mb-3">
              {gameState.moves} {t("game.moves")} · {formatTime(gameState.elapsedSeconds)}
            </p>

            <div className="text-sm w-full mb-3 space-y-0.5">
              <div className="flex justify-between">
                <span className="text-white/70">{t("win.timeBonus", { seconds: remainingSeconds })}</span>
                <span className="text-green-300 font-medium">+{breakdown.timeBonus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t("win.foundation52")}</span>
                <span className="text-green-300 font-medium">+{breakdown.foundationBonus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t("win.winBonus")}</span>
                <span className="text-green-300 font-medium">+{breakdown.winBonus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">{t("end.flips", { count: gameState.flipCount })}</span>
                <span className="text-green-300 font-medium">+{breakdown.flipBonus}</span>
              </div>

              {(breakdown.rotationPenalty > 0 || breakdown.shakePenalty > 0) && (
                <div className="border-t border-white/15 my-1" />
              )}
              {breakdown.rotationPenalty > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/70">{t("end.rotations", { count: extraRotations })}</span>
                  <span className="text-red-300 font-medium">−{breakdown.rotationPenalty}</span>
                </div>
              )}
              {breakdown.shakePenalty > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/70">{t("end.shakes", { count: gameState.shakeCount })}</span>
                  <span className="text-red-300 font-medium">−{breakdown.shakePenalty}</span>
                </div>
              )}

              <div className="border-t border-white/30 pt-1.5 mt-1 flex justify-between items-baseline">
                <span className="text-white/80 font-semibold">{t("end.total")}</span>
                <span className="text-yellow-300 text-2xl font-bold">{breakdown.total.toLocaleString(numFmt)}</span>
              </div>
            </div>

            {user && (
              <p className="text-sm mb-3">
                {savedRank === null
                  ? <span className="text-white/40">{t("end.saving")}</span>
                  : <span className="text-yellow-300 font-bold">{t("end.rank", { rank: savedRank })}</span>
                }
              </p>
            )}

            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate(`/leaderboard?seed=${gameState.seed}${drawMode === 3 ? "&drawMode=3" : ""}`)}
                className="px-6 py-2 rounded-lg bg-yellow-400/20 hover:bg-yellow-400/30 text-yellow-300 text-sm font-medium transition-colors border border-yellow-400/30"
              >
                {t("end.showLeaderboard")}
              </button>
              {!isDaily && mode !== "klondike" && (
                <button onClick={handleNext} className="px-6 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
                  {t("end.nextSet")}
                </button>
              )}
              <button onClick={handleRetry} className="px-4 py-1.5 rounded-lg text-white/50 hover:text-white/80 text-xs transition-colors">
                {t("end.retry")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
